import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { UpsertGoalModal } from '../../components/UpsertGoalModal/UpsertGoalModal';
import { FormValues, HomePurchaseGoalData, UpsertDTO } from './HomePurchase.meta';
import { useHomePurchase } from './useHomePurchase';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { useUpsertGoalMutation } from '../../api/useUpsertGoalMutation';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { AT_RETIREMENT } from '../../Goals.meta';
import { GoalComponentProps } from '../../components/Goal/Goal.meta';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { AssetBucketingSelect } from '../../components/AssetBucketingSelect/AssetBucketingSelect';

const FORM_ID = 'ProfileGoals--HomePurchase';

interface Props extends GoalComponentProps {
  goalData: HomePurchaseGoalData;
}

export const HomePurchase = ({
  clientId,
  goalData,
  assetBucketingIndices,
  yearsOptions,
  mortgageTypeOptions,
  ...rest
}: Props) => {
  const {
    desiredPurchaseYear,
    purchaseYearsOptions,
    mortgageOptions,
    purchaseAtRetirement,
    downPayment,
    marketValue,
    mortgageType,
    interestRate,
    defaultShowAdvanced,
    assetGrowth,
    propertyTax,
    pmi,
    homeInsurancePerYear,
    monthlyHoa,
  } = useHomePurchase({
    goalData,
    yearsOptions,
    mortgageTypeOptions,
  });

  const [showAdvanced, setShowAdvanced] = useState(defaultShowAdvanced);

  const { register, control, watch, handleSubmit, setValue } = useForm<FormValues>({
    defaultValues: {
      name: rest.name,
      desiredPurchaseYear: purchaseAtRetirement ? AT_RETIREMENT : desiredPurchaseYear,
      purchaseAtRetirement: !!purchaseAtRetirement,
      downPayment,
      marketValue,
      mortgageType,
      interestRate,
      assetGrowth,
      propertyTax,
      pmi,
      homeInsurancePerYear,
      monthlyHoa,
      assetBucketingIndices,
    },
  });

  const hasMortgage = watch('mortgageType') !== null;

  const {
    mutateAsync: upsertGoal,
    isLoading: isUpsertingGoal,
    error: upsertGoalError,
  } = useUpsertGoalMutation({ clientId, url: '/Consumer/GoalUpdate/SaveHomePurchaseGoal' });

  const onSubmit = async (values: FormValues) => {
    try {
      const upsertDTO: UpsertDTO = {
        ...values,
        desiredPurchaseYear: values.purchaseAtRetirement ? null : values.desiredPurchaseYear,
        id: rest.id,
      };

      await upsertGoal(upsertDTO);

      rest.onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <UpsertGoalModal clientId={clientId} formId={FORM_ID} isLoading={isUpsertingGoal} {...rest}>
      {upsertGoalError && <Alert message={upsertGoalError.message} variant="error" mb="16" />}

      <Box
        id={FORM_ID}
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        display="grid"
        css={`
          grid-template-columns: 1fr;
          gap: 24px;
        `}
        aria-label="Home purchase goal form"
      >
        <Input {...register('name')} label="Property Name" maxLength={27} />

        <Controller
          control={control}
          name="desiredPurchaseYear"
          render={({ field: { value, onChange } }) => (
            <Select
              label="Desired Purchase Year"
              value={value ? ({ value, label: value } as any) : null}
              options={purchaseYearsOptions}
              onChange={opt => {
                onChange(opt?.value);
                setValue('purchaseAtRetirement', opt?.value === AT_RETIREMENT);
              }}
              isPortal
            />
          )}
        />

        <Box
          display="grid"
          css={`
            grid-template-columns: 1fr 1fr;
            gap: 24px 16px;
          `}
        >
          <Controller
            control={control}
            name="downPayment"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                label="Down Payment"
                leftIcon="far fa-dollar-sign"
                decimalScale={2}
                allowNegative={false}
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                tooltip="Financial experts generally suggest putting down 20% of the cost of the home in cash in order to save on mortgage insurance."
                {...field}
              />
            )}
          />

          <Controller
            control={control}
            name="marketValue"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                label="Market Value"
                leftIcon="far fa-dollar-sign"
                decimalScale={2}
                allowNegative={false}
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                {...field}
              />
            )}
          />
        </Box>

        <Box
          display="grid"
          css={`
            grid-template-columns: repeat(${hasMortgage ? 2 : 1}, 1fr);
            gap: 24px 16px;
          `}
        >
          <Controller
            control={control}
            name="mortgageType"
            render={({ field: { value, onChange } }) => (
              <Select
                label="Mortgage Type"
                value={mortgageOptions.find(({ value: v }) => value === v)}
                options={mortgageOptions}
                onChange={opt => onChange(opt?.value)}
                isPortal
              />
            )}
          />

          {hasMortgage && (
            <Controller
              control={control}
              name="interestRate"
              render={({ field: { onChange, ...field } }) => (
                <InputNumberMask
                  label="Interest Rate"
                  rightIcon="far fa-percent"
                  decimalScale={3}
                  allowNegative={false}
                  onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                  {...field}
                />
              )}
            />
          )}
        </Box>

        <Button
          variant="link"
          leftIcon={showAdvanced ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
          onClick={() => setShowAdvanced(!showAdvanced)}
          aria-expanded={showAdvanced}
          size="small"
          mr="auto"
        >
          {showAdvanced ? 'Hide' : 'Show'} Advanced
        </Button>

        {showAdvanced && (
          <>
            <Controller
              control={control}
              name="assetGrowth"
              render={({ field: { onChange, ...field } }) => (
                <InputNumberMask
                  label="Asset Growth Rate"
                  rightIcon="far fa-percent"
                  decimalScale={2}
                  allowNegative={false}
                  onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                  tooltip="The rate at which the asset’s market value will grow."
                  {...field}
                />
              )}
            />

            <Controller
              control={control}
              name="propertyTax"
              render={({ field: { onChange, ...field } }) => (
                <InputNumberMask
                  label="Property Tax"
                  leftIcon="far fa-dollar-sign"
                  decimalScale={2}
                  allowNegative={false}
                  onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                  {...field}
                />
              )}
            />

            {hasMortgage && (
              <Controller
                control={control}
                name="pmi"
                render={({ field: { onChange, ...field } }) => (
                  <InputNumberMask
                    label="PMI"
                    rightIcon="far fa-percent"
                    decimalScale={2}
                    allowNegative={false}
                    onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                    tooltip="Private mortgage insurance, also called PMI, is a type of mortgage insurance you might be required to pay for if you have a conventional loan. Like other kinds of mortgage insurance, PMI protects the lender if you stop making payments on your loan."
                    {...field}
                  />
                )}
              />
            )}

            <Box
              display="grid"
              css={`
                grid-template-columns: 1fr 1fr;
                gap: 24px 16px;
              `}
            >
              <Controller
                control={control}
                name="homeInsurancePerYear"
                render={({ field: { onChange, ...field } }) => (
                  <InputNumberMask
                    label="Home Insurance"
                    leftIcon="far fa-dollar-sign"
                    innerText="/year"
                    decimalScale={2}
                    allowNegative={false}
                    onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                    {...field}
                  />
                )}
              />

              <Controller
                control={control}
                name="monthlyHoa"
                render={({ field: { onChange, ...field } }) => (
                  <InputNumberMask
                    label="Monthly HOA"
                    leftIcon="far fa-dollar-sign"
                    innerText="/month"
                    decimalScale={2}
                    allowNegative={false}
                    onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                    {...field}
                  />
                )}
              />
            </Box>
          </>
        )}

        <AssetBucketingSelect clientId={clientId} control={control} />
      </Box>
    </UpsertGoalModal>
  );
};
